import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { createScopedStorage } from "./workspaceStore";
import { getFavoriteIndicatorsKey } from "../workspace/constants";

// Default favorite indicators - 10 most essential and commonly used indicators
// Selected based on universal usage patterns and MVP gap analysis
const DEFAULT_FAVORITES = [
  "sma", // Simple Moving Average - most fundamental indicator
  "ema", // Exponential Moving Average - widely used trend indicator
  "bollingerBand", // Bollinger Bands - classic volatility/overbought-oversold indicator
  "rsi", // RSI - most popular oscillator for overbought/oversold conditions
  "macd", // MACD - classic trend-following momentum indicator
  "stochastic", // Stochastic Oscillator - popular momentum indicator
  "adx", // ADX - measures trend strength (high priority from MVP)
  "atr", // Average True Range - essential volatility measure
  "onBalanceVolume", // OBV - volume-based momentum indicator
  "cci", // Commodity Channel Index - versatile oscillator
];

export const useFavoriteIndicatorsStore = create(
  persist(
    (set) => ({
      favorites: DEFAULT_FAVORITES,
      addFavorite: (indicatorKey) =>
        set((state) => ({
          favorites: state.favorites.includes(indicatorKey)
            ? state.favorites
            : [...state.favorites, indicatorKey],
        })),
      removeFavorite: (indicatorKey) =>
        set((state) => ({
          favorites: state.favorites.filter((key) => key !== indicatorKey),
        })),
      toggleFavorite: (indicatorKey) =>
        set((state) => ({
          favorites: state.favorites.includes(indicatorKey)
            ? state.favorites.filter((key) => key !== indicatorKey)
            : [...state.favorites, indicatorKey],
        })),
    }),
    {
      name: "favorite-indicators", // Used as fallback name, actual key is dynamic (user-scoped)
      storage: createJSONStorage(() =>
        createScopedStorage(getFavoriteIndicatorsKey),
      ),
      version: 1, // Increment to force migration
      migrate: (persistedState, version) => {
        // Migration logic for version updates
        if (
          version === 0 ||
          !persistedState.favorites ||
          persistedState.favorites.length === 0
        ) {
          return {
            ...persistedState,
            favorites: DEFAULT_FAVORITES,
          };
        }
        return persistedState;
      },
    },
  ),
);
